// Small helper for the per-column JSON blob API (GET/PATCH /characters/:id/:blob) —
// the JSON sibling of TurboFetch (which speaks turbo-stream). Centralizes the fetch
// contract for the blob endpoints so CSRF, headers, credentials, and error handling
// live in ONE place (a botched CSRF header is a silent 422 — worth not re-typing).
//
//   const res = await JsonFetch.get("/characters/npcs/x/skills")
//   res.ok ? res.data : null            // caller decides its failure policy
//   const w = await JsonFetch.patch("/characters/npcs/x/state", { chips: ["y"] })
//   w.ok / w.status                     // did the write land?
//
// get() and patch() BOTH return a JsonResponse (ok/status/data + chaining) — the same
// policy-neutral contract as TurboFetch. The transport SURFACES the outcome; it never
// swallows failure into a bare value, so a layer (e.g. a caching wrapper) can add its own
// throw/cache policy on top.

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}

// Result wrapper — ok/status/data (parsed JSON body when present) + the TurboFetch-style
// onSuccess/onFailure/onError chain. Both get() and patch() return this.
class JsonResponse {
  constructor(ok, status, data = null, error = null) {
    this.ok = ok
    this.status = status
    this.data = data // parsed JSON body (a GET's blob, or a write's error body); null if none
    this.error = error
    if (error) console.error("[JsonFetch]", error)
  }

  onSuccess(fn) {
    if (this.ok) fn(this)
    return this
  }

  onFailure(fn) {
    if (!this.ok && !this.error) fn(this)
    return this
  }

  onError(fn) {
    if (this.error) fn(this.error)
    return this
  }
}

const JsonFetch = {
  // GET a blob. Returns a JsonResponse — res.data is the parsed JSON on success.
  // Failure (non-ok / network error) is SURFACED (ok=false), not swallowed, so callers
  // (and wrapping layers) choose their own policy: `res.ok ? res.data : []` degrades;
  // a caching layer can throw + skip caching on !ok.
  async get(url) {
    try {
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      const data = res.ok ? await res.json().catch(() => null) : null
      return new JsonResponse(res.ok, res.status, data)
    } catch (error) {
      return new JsonResponse(false, null, null, error)
    }
  },

  // PATCH a blob — sends `body` as JSON with CSRF. Returns a JsonResponse; res.data is any
  // parsed error body. The blob endpoints answer 204 on success (no body), 400/422 on a
  // bad body.
  async patch(url, body) {
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      })
      const data = res.status === 204 ? null : await res.json().catch(() => null)
      return new JsonResponse(res.ok, res.status, data)
    } catch (error) {
      return new JsonResponse(false, null, null, error)
    }
  },
}

export default JsonFetch
